import { computed, watch } from 'vue';

/**
 * Reglas fiscales de la ficha de compañía (Verifactu).
 *
 * Fuentes, por orden de autoridad:
 *  1. `CreateCompaniesRequest` / `UpdateCompaniesRequest` de laravel.api-backend
 *     (lo que la API acepta o rechaza hoy; tiene tests que fijan la matriz).
 *  2. Documentación oficial de la AEAT:
 *     - Documento de validaciones y errores, §15.5 (`OperacionExenta`) y §15.6
 *       (`ClaveRegimen`), más los códigos 1199, 1237, 1238, 1245, 1252 y 1260.
 *     - FAQ para empresas de desarrollo, pregunta 26 ("Contenido de la factura:
 *       IVA, IGIC o IPSI"), que fija que con IPSI no se informa `ClaveRegimen`.
 *
 * Cada regla cita su código AEAT para poder correlacionar un rechazo con la
 * especificación. Este composable no toca la vista: expone catálogos,
 * computados y la validación previa al envío.
 */

export const TIPO_IMPUESTO_IVA = 'iva';
export const TIPO_IMPUESTO_IPSI = 'ipsi';
export const TIPO_IMPUESTO_IGIC = 'igci';
export const TIPO_IMPUESTO_OTROS = 'otros';

/**
 * Tipos de impuesto aceptados por la API (`in:iva,ipsi,igci,otros`).
 *
 * El `value` es el que se persiste en `teatro_companies.tipo_impuesto` y debe
 * ser EXACTAMENTE el que valida la API: `igci` (no `igic`). El builder de
 * facturas tolera ambas grafías por compatibilidad, pero el FormRequest solo
 * acepta `igci`, así que enviar `igic` devuelve 422.
 */
export const TIPOS_IMPUESTO = [
    { value: TIPO_IMPUESTO_IVA, label: 'IVA' },
    { value: TIPO_IMPUESTO_IPSI, label: 'IPSI' },
    { value: TIPO_IMPUESTO_IGIC, label: 'IGIC' },
    { value: TIPO_IMPUESTO_OTROS, label: 'Otros' },
];

/**
 * Claves de régimen del catálogo. Debe coincidir EXACTAMENTE con la regla
 * `in:` de la API, que sigue el XSD de la AEAT
 * (`SuministroInformacion.xsd` → `IdOperacionesTrascendenciaTributariaType`):
 * 01..11, 14, 15, 17, 18, 19 y 20.
 *
 * La clave 21 existe en la AEAT desde la v1.1.2 (régimen simplificado IGIC),
 * pero el XSD y la validación de la API todavía no la admiten, así que no se
 * ofrece: elegirla devolvería 422.
 */
export const CLAVES_REGIMEN = [
    { value: '01', label: '01 - Operación de régimen general' },
    { value: '02', label: '02 - Exportación' },
    { value: '03', label: '03 - Régimen especial de bienes usados, objetos de arte, antigüedades y objetos de colección' },
    { value: '04', label: '04 - Régimen especial del oro de inversión' },
    { value: '05', label: '05 - Régimen especial de las agencias de viajes' },
    { value: '06', label: '06 - Régimen especial grupo de entidades en IVA o IGIC (Nivel Avanzado)' },
    { value: '07', label: '07 - Régimen especial del criterio de caja' },
    { value: '08', label: '08 - Operaciones sujetas al IPSI/IVA o IGIC' },
    { value: '09', label: '09 - Mediadores de agencias de viaje (D.A.4ª RD1619/2012)' },
    { value: '10', label: '10 - Cobros por cuenta de terceros' },
    { value: '11', label: '11 - Operaciones de arrendamiento de local de negocio' },
    { value: '14', label: '14 - IVA/IGIC pendiente de devengo en certificaciones de obra (AAPP)' },
    { value: '15', label: '15 - IVA/IGIC pendiente de devengo en operaciones de tracto sucesivo' },
    { value: '17', label: '17 - OSS, IOSS o régimen especial de comerciante minorista' },
    { value: '18', label: '18 - Recargo de equivalencia / régimen especial pequeño empresario o profesional' },
    { value: '19', label: '19 - REAGYP / operaciones interiores exentas art. 25 Ley 19/1994' },
    { value: '20', label: '20 - Régimen simplificado / operaciones sujetas al IPSI' },
];

/**
 * Claves que no se ofrecen en la ficha:
 *  - 06 (grupo de entidades): exige tipo de factura distinto de F2/F3/R5 y
 *    `base_imponible_a_coste` (AEAT 1202). Facturamos siempre como F2
 *    simplificada y no enviamos el coste, así que AEAT rechazaría todas las
 *    facturas de la compañía.
 *  - 10 (cobros por cuenta de terceros): exige N1, F1 y destinatario con NIF
 *    (AEAT 1205). Emitimos S1, F2 y sin datos de cliente.
 *  - 14 (IVA pendiente en certificaciones de obra a una AAPP): exige
 *    `fecha_operacion` y tipo F1/R1..R4 (AEAT 1147/1148). No mandamos ninguno.
 *  - 17 (OSS/IOSS) y 18 (recargo de equivalencia): sí emiten con nuestra forma
 *    de facturar, pero exigen campos que esta ficha no configura (clave 17 pide
 *    destinatarios; 18 pide los importes del recargo en el desglose). Se
 *    ocultan por decisión de producto.
 *
 * Las tres primeras están alineadas con
 * `VerifactuInvoiceBuilder::MOTIVOS_REGIMEN_INCOMPATIBLE` del backend, que las
 * rechaza al guardar la compañía con el motivo AEAT de cada una.
 */
export const CLAVES_REGIMEN_SIN_SOPORTE = ['06', '10', '14', '17', '18'];

/**
 * Causas de exención por tipo de impuesto. La lista base es L10; E7 y E8 son
 * exclusivas de IGIC (AEAT v1.2.2, sección 15.5).
 */
export const EXENTAS_POR_IMPUESTO = {
    [TIPO_IMPUESTO_IVA]: [
        { value: 'E1', label: 'E1 - Operación exenta según art. 20 Ley 37/1992' },
        { value: 'E2', label: 'E2 - Operación exenta según art. 21 Ley 37/1992' },
        { value: 'E3', label: 'E3 - Operación exenta según art. 22 Ley 37/1992' },
        { value: 'E4', label: 'E4 - Operación exenta según arts. 23 y 24 Ley 37/1992' },
        { value: 'E5', label: 'E5 - Operación exenta según art. 25 Ley 37/1992' },
        { value: 'E6', label: 'E6 - Operación exenta según Ley 37/1992 (resto de exenciones)' },
    ],
    [TIPO_IMPUESTO_IGIC]: [
        { value: 'E1', label: 'E1 - Exenta según capítulo I Decreto Legislativo 1/2025' },
        { value: 'E2', label: 'E2 - Exenta según art. 11 Ley 20/1991' },
        { value: 'E3', label: 'E3 - Exenta según art. 12 Ley 20/1991' },
        { value: 'E4', label: 'E4 - Exenta según art. 13 Ley 20/1991' },
        { value: 'E5', label: 'E5 - Exenta según art. 25 Ley 19/1994' },
        { value: 'E6', label: 'E6 - Exenta según art. 47 Ley 19/1994' },
        { value: 'E7', label: 'E7 - Exenta según art. 90 Decreto Legislativo 1/2025' },
        { value: 'E8', label: 'E8 - Exenta según Ley 20/1991 (resto de exenciones)' },
    ],
    [TIPO_IMPUESTO_IPSI]: [
        { value: 'E1', label: 'E1 - Exenta según art. 7 Ley 8/1991' },
        { value: 'E2', label: 'E2 - Exenta según art. 8 Ley 8/1991' },
        { value: 'E3', label: 'E3 - Exenta según art. 8 Ley 8/1991' },
        { value: 'E4', label: 'E4 - Exenta según arts. 9 y 10 Ley 8/1991' },
        { value: 'E5', label: 'E5 - Exenta según art. 8 Ley 8/1991' },
        { value: 'E6', label: 'E6 - Exenta según Ley 8/1991 (resto de exenciones)' },
    ],
};

/** Porcentajes de IVA admitidos por la AEAT para S1 (sección 15.1). */
export const VALORES_IVA = [0, 2, 4, 5, 7.5, 10, 21];

const CLAVE_EXPORTACION = '02';
const CLAVE_REGIMEN_GENERAL = '01';
const CLAVE_CRITERIO_CAJA = '07';
const CLAVE_IPSI_IGIC = '08';

/**
 * Impuestos que admiten `clave_regimen`. La FAQ 26 lo dice literalmente: con
 * IPSI no se informa la clave de régimen. La API lo aplica igual
 * (`Create/UpdateCompaniesRequest` solo la admite con `iva` o `igci`, con un
 * test que fija el 422 para IPSI).
 */
const IMPUESTOS_CON_CLAVE_REGIMEN = [TIPO_IMPUESTO_IVA, TIPO_IMPUESTO_IGIC];
/** Impuestos que admiten `operacion_exenta` (AEAT 15.5 + validación de la API). */
const IMPUESTOS_CON_EXENTA = [TIPO_IMPUESTO_IVA, TIPO_IMPUESTO_IGIC, TIPO_IMPUESTO_IPSI];

/** Exenciones vetadas por el régimen de criterio de caja (AEAT 1203). */
const EXENTAS_VETADAS_POR_CAJA = ['E2', 'E3', 'E4', 'E5'];
/** Exenciones vetadas por el régimen general (AEAT 1199). */
const EXENTAS_VETADAS_POR_GENERAL = ['E2', 'E3'];

/**
 * Normaliza el tipo de impuesto. `igic` es un typo que la API no acepta
 * (`in:iva,ipsi,igci,otros`): se traduce a `igci` para no enviar un valor que
 * devolvería 422.
 */
export function normalizarTipoImpuesto(valor) {
    const tipo = String(valor || '').toLowerCase();

    return tipo === 'igic' ? TIPO_IMPUESTO_IGIC : tipo;
}

/**
 * True si la causa de exención queda vetada por la clave de régimen. Cada rama
 * cita la regla que la motiva.
 *
 * No se veta E2/E3 fuera del régimen 02: la API de compañías no lo exige y
 * tiene un test que fija la aceptación de E2 con clave 14. (La API de facturas
 * sí ata E2 al régimen 02, pero eso se comprueba al emitir, no al guardar.)
 */
export function exentaVetadaPorClave(clave, exenta) {
    if (!clave || !exenta) {
        return false;
    }
    // AEAT 1252: el régimen 08 emite la línea como N2, y el XSD hace
    // excluyentes N2 y operacion_exenta.
    if (clave === CLAVE_IPSI_IGIC) {
        return true;
    }
    // AEAT 1203: criterio de caja, sin E2/E3/E4/E5.
    if (clave === CLAVE_CRITERIO_CAJA && EXENTAS_VETADAS_POR_CAJA.includes(exenta)) {
        return true;
    }
    // AEAT 1199: régimen general, sin exportaciones ni entregas
    // intracomunitarias.
    if (clave === CLAVE_REGIMEN_GENERAL && EXENTAS_VETADAS_POR_GENERAL.includes(exenta)) {
        return true;
    }
    // Regla de proyecto que mantiene la ficha: con el régimen 02 la operación
    // debe declararse como exportación E2. (La API de compañías no la impone;
    // la de facturas sí ata E2 al régimen 02 al emitir.)
    if (clave === CLAVE_EXPORTACION && exenta !== 'E2') {
        return true;
    }

    return false;
}

/**
 * @param {object} compania Objeto compartido de la compañía (el formulario lo
 *   muta; el padre lo lee al recibir `terminado`).
 */
export function useCompaniaFiscal(compania) {
    // Una fila guardada con la grafía heredada `igic` se corrige a `igci` al
    // montar: es el único valor que valida la API, y así el select la muestra
    // seleccionada en vez de quedar vacío.
    const tipoNormalizado = normalizarTipoImpuesto(compania && compania.tipo_impuesto);
    if (compania && compania.tipo_impuesto !== tipoNormalizado) {
        compania.tipo_impuesto = tipoNormalizado;
    }

    // El formulario asume IVA cuando la compañía no tiene `tipo_impuesto`
    // (filas anteriores a la columna): el backend factura como IVA por defecto
    // (VerifactuInvoiceBuilder::tipoImpuestoAeat). Este computado controla lo
    // que se MUESTRA.
    const tipoImpuestoSeleccionado = computed(() => {
        const tipo = normalizarTipoImpuesto(compania && compania.tipo_impuesto);

        return tipo || TIPO_IMPUESTO_IVA;
    });

    // El tipo que se PERSISTIRÁ: el explícito, o `iva` solo cuando una fila
    // antigua sin tipo configura clave_regimen/operacion_exenta (la API
    // rechaza ambos con el tipo vacío).
    const tipoEfectivo = computed(() => {
        const tipo = normalizarTipoImpuesto(compania && compania.tipo_impuesto);

        return tipo || ((compania.clave_regimen || compania.operacion_exenta) ? TIPO_IMPUESTO_IVA : '');
    });

    const claveRegimenSeleccionada = computed(() => (compania && compania.clave_regimen) || '');
    const exentaSeleccionada = computed(() => (compania && compania.operacion_exenta) || '');

    // `clave_regimen` solo con IVA o IGIC. Con IPSI no se informa (FAQ 26, que
    // cita textualmente la regla: si Impuesto = "02" no se rellena ClaveRegimen),
    // y la API lo rechaza con 422.
    const permiteClaveRegimen = computed(() =>
        IMPUESTOS_CON_CLAVE_REGIMEN.includes(tipoImpuestoSeleccionado.value)
    );
    const permiteOperacionExenta = computed(() =>
        IMPUESTOS_CON_EXENTA.includes(tipoImpuestoSeleccionado.value)
    );

    // Con clave 08 la operación se declara no sujeta (N2), excluyente con la
    // exención: el campo ni se ofrece.
    const ofreceOperacionExenta = computed(() =>
        permiteOperacionExenta.value && claveRegimenSeleccionada.value !== CLAVE_IPSI_IGIC
    );

    const ivaLabel = computed(() => {
        const tipo = TIPOS_IMPUESTO.find(t => t.value === tipoImpuestoSeleccionado.value);

        return 'Porcentaje ' + (tipo && tipo.value ? tipo.label : 'Impuesto');
    });

    const ivaEsCero = computed(() => {
        const iva = parseFloat(compania.iva);

        return Number.isFinite(iva) && iva === 0;
    });

    const tipoImpuestoEsIva = computed(() => tipoEfectivo.value === TIPO_IMPUESTO_IVA);

    // Claves deshabilitadas: las que exigen campos que esta ficha no configura.
    // Se ofrecen deshabilitadas (en vez de desaparecer) para que el usuario vea
    // que existen y por qué no puede elegirlas.
    const clavesRegimenDeshabilitadas = computed(() => CLAVES_REGIMEN_SIN_SOPORTE);

    const avisoClaveRegimen = computed(() => {
        if (claveRegimenSeleccionada.value === CLAVE_REGIMEN_GENERAL) {
            return 'Con clave 01 no se admiten las exenciones E2 ni E3.';
        }
        if (claveRegimenSeleccionada.value === CLAVE_CRITERIO_CAJA) {
            return 'Con clave 07 (criterio de caja) no se admiten E2, E3, E4 ni E5.';
        }
        if (claveRegimenSeleccionada.value === CLAVE_EXPORTACION) {
            return 'Con clave 02 (exportación) la operación exenta debe ser E2.';
        }

        return '';
    });

    // Causas vetadas por la clave de régimen. Se pasan como `disabled-values`
    // para que sigan visibles con su motivo, en vez de desaparecer del listado
    // sin explicación.
    const exentasDeshabilitadas = computed(() =>
        (EXENTAS_POR_IMPUESTO[tipoImpuestoSeleccionado.value] || [])
            .filter(exenta => exentaVetadaPorClave(claveRegimenSeleccionada.value, exenta.value))
            .map(exenta => exenta.value)
    );

    const exentasPermitidas = computed(() => EXENTAS_POR_IMPUESTO[tipoImpuestoSeleccionado.value] || []);

    const avisoExenta = computed(() => {
        const clave = claveRegimenSeleccionada.value;

        if (!clave || clave === CLAVE_EXPORTACION) {
            return '';
        }
        if (clave === CLAVE_REGIMEN_GENERAL) {
            return 'Con clave 01 no se admiten E2 ni E3.';
        }
        if (clave === CLAVE_CRITERIO_CAJA) {
            return 'Con clave 07 (criterio de caja) no se admiten E2, E3, E4 ni E5.';
        }

        return 'Con clave 02 (exportación) la operación exenta debe ser E2.';
    });

    const ivaRules = computed(() => (exentaSeleccionada.value ? 'numeric' : 'required|numeric'));

    /**
     * True si la exención es admisible con ese tipo y esa clave.
     */
    function exentaPermitida(tipo, clave, exenta) {
        if (!exenta) {
            return true;
        }
        if (!IMPUESTOS_CON_EXENTA.includes(tipo)) {
            return false;
        }
        const catalogo = EXENTAS_POR_IMPUESTO[tipo] || [];

        return catalogo.some(e => e.value === exenta) && !exentaVetadaPorClave(clave, exenta);
    }

    /**
     * Descarta las selecciones que el estado actual invalida.
     *
     * Se ejecuta al montar y en cada cambio de tipo de impuesto o de clave de
     * régimen: sin esto, un campo que deja de ofrecerse (la exención con la
     * clave 08, o la clave con "Otros") conservaría su valor oculto y el
     * formulario quedaría en un estado que la API rechaza al guardar, sin que
     * el usuario pueda corregirlo desde la interfaz.
     */
    function depurarSelecciones() {
        const tipo = tipoImpuestoSeleccionado.value;

        if (compania.clave_regimen && !permiteClaveRegimen.value) {
            compania.clave_regimen = '';
        }

        // La clave de régimen es OBLIGATORIA con IVA e IGIC (AEAT 1245/1260), y
        // el régimen general (01) es el caso corriente, así que se
        // preselecciona en vez de dejar el campo vacío. Aplica al tipo
        // MOSTRADO, así que el formulario de creación (que arranca sin tipo y
        // se muestra como IVA) también lo trae.
        //
        // Cautela: no se preselecciona si eso invalidaría una exención ya
        // guardada (E2 no está admitida con 01). Ahí se deja vacío para que el
        // usuario elija, en vez de borrarle la exención por sorpresa.
        if (
            !compania.clave_regimen
            && permiteClaveRegimen.value
            && !exentaVetadaPorClave(CLAVE_REGIMEN_GENERAL, compania.operacion_exenta)
        ) {
            compania.clave_regimen = CLAVE_REGIMEN_GENERAL;
        }

        if (
            compania.operacion_exenta
            && !exentaPermitida(tipo, compania.clave_regimen, compania.operacion_exenta)
        ) {
            compania.operacion_exenta = '';
        }
    }

    // Último porcentaje antes de elegir una exención: al deseleccionarla se
    // restaura para no obligar a reescribirlo. `immediate` deja el campo limpio
    // y deshabilitado cuando la compañía ya tiene una exención cargada.
    let ivaPrevioExenta = null;

    function sincronizarIvaConExenta(nuevo, anterior) {
        if (nuevo === anterior) {
            return;
        }
        if (nuevo) {
            // La API rechaza tipo_impositivo junto a operacion_exenta, así que
            // el porcentaje se vacía y el campo queda deshabilitado.
            ivaPrevioExenta = compania.iva;
            if (compania.iva !== null && compania.iva !== undefined && compania.iva !== '') {
                compania.iva = null;
            }
        } else if (anterior !== undefined) {
            // El guard `anterior !== undefined` evita que el arranque
            // `immediate` de una compañía sin exención borre el IVA persistido.
            compania.iva = ivaPrevioExenta ?? null;
            ivaPrevioExenta = null;
        }
    }

    /**
     * Validación previa al envío. Devuelve el mensaje de error, o null si la
     * combinación es válida.
     */
    function validar() {
        const tipo = tipoImpuestoSeleccionado.value;
        const clave = compania.clave_regimen;
        const exenta = compania.operacion_exenta;

        if (exenta && !permiteOperacionExenta.value) {
            return 'La operación exenta requiere un tipo de impuesto válido (IVA, IGIC o IPSI).';
        }
        if (clave && !permiteClaveRegimen.value) {
            return 'La clave de régimen solo está permitida cuando el tipo de impuesto es IVA o IGIC.';
        }
        if (CLAVES_REGIMEN_SIN_SOPORTE.includes(clave)) {
            return 'La clave de régimen ' + clave + ' no está disponible: requiere campos que no se configuran aquí.';
        }
        if (tipoImpuestoEsIva.value && !exenta && !VALORES_IVA.includes(parseFloat(compania.iva))) {
            return 'El valor de IVA debe ser uno de: ' + VALORES_IVA.join(', ');
        }
        if (ivaEsCero.value && tipoEfectivo.value && permiteOperacionExenta.value && !exenta) {
            return 'Con tipo impositivo 0 la operación es exenta: indica la causa de exención.';
        }
        if (clave === CLAVE_EXPORTACION && exenta !== 'E2') {
            return 'Con clave de régimen 02 (exportación) la operación exenta debe ser E2.';
        }
        if (exenta) {
            const catalogo = EXENTAS_POR_IMPUESTO[tipo] || [];
            if (!catalogo.some(e => e.value === exenta)) {
                return 'El valor de operación exenta no es válido para el tipo de impuesto seleccionado.';
            }
            if (exentaVetadaPorClave(clave, exenta)) {
                return 'La operación exenta ' + exenta + ' no está permitida con la clave de régimen seleccionada.';
            }
        }

        return null;
    }

    watch(tipoImpuestoSeleccionado, depurarSelecciones);
    watch(claveRegimenSeleccionada, depurarSelecciones);
    watch(exentaSeleccionada, sincronizarIvaConExenta, { immediate: true });
    depurarSelecciones();

    return {
        tiposImpuesto: TIPOS_IMPUESTO,
        clavesRegimenCatalogo: CLAVES_REGIMEN,
        clavesRegimenDeshabilitadas,
        exentasPermitidas,
        exentasDeshabilitadas,
        tipoImpuestoSeleccionado,
        permiteClaveRegimen,
        ofreceOperacionExenta,
        avisoClaveRegimen,
        avisoExenta,
        ivaLabel,
        ivaRules,
        validar,
    };
}
